import { useRef, useState } from 'react'
// import { Link } from 'react-router-dom'  // Platform Defaults — commented out below
import { useTranslation } from 'react-i18next'
import { Field } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
// import { ForgeDialog } from '@/components/forge/ForgeDialog'  // Danger Zone
// import { ForgeSelect } from '@/components/forge/ForgeSelect'  // Regional / Defaults
import { Panel } from '@/components/forge/Panel'
import { ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow } from '@/components/forge/primitives'
import { useAccount, useSaveAccount } from '@/features/org/account'
import { FILE_PURPOSES, acceptFor, checkFile, uploadFile } from '@/features/files/api'
import { usePermission } from '@/features/auth/permissions'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import defaultLogo from '@/assets/pragmatyc_logo.png'
/* Commented out with the cards that used them — see the header. Left as an
   import block rather than deleted so restoring a card is one edit here and one
   there, not a hunt for what it needed.

import {
  fetchExport,
  useOrgSettings,
  useResetSettings,
  useSaveOrgSettings,
} from '@/features/org/settings'
import { useScope } from '@/providers/ScopeProvider'
import { SUPPORTED_LOCALES } from '@/i18n'
import { downloadJson } from '@/lib/csv'
*/

/**
 * Settings — General.
 *
 * ── What this page is now ────────────────────────────────────────────────
 * `GET /v1/account` and `PUT /v1/account`, and those carry three editable
 * fields between them: the login `alias`, the `org_name`, and a `logo_file_id`.
 * That is the entire account as far as this API is concerned, so that is the
 * entire page.
 *
 * ── Why the rest is commented out rather than deleted ────────────────────
 * The design has four cards and eleven controls. Seven of those have no field
 * on the account and nothing to write to: display name, industry, tagline, the
 * three regional preferences, the language, the three platform defaults, and
 * the danger zone's three actions. They were fixture-backed, which on a
 * settings page is the worst thing to be — a control that appears to save and
 * does not.
 *
 * They stay here as comments because this is the design's page and the fields
 * may yet land. Restoring one is uncommenting a block whose wiring is intact,
 * rather than rebuilding it from the design. The same reasoning the OEE filter
 * bar's compare control was left under.
 *
 * ── The regional controls are gone for a second reason ───────────────────
 * Not only that the account has no time zone — the PRODUCT does not hold one at
 * the organisation. `DisplayPreferenceFields` is mixed into `User` and `Plant`
 * and nothing else, so a time zone belongs to a person or to a plant. The
 * person's copy is real and editable, in the profile dialog off the nav rail.
 * Keeping an org-level set here would mean two screens over one preference
 * where only one of them writes anywhere.
 *
 * ── Both routes need `account.manage` ────────────────────────────────────
 * Checked from the permission map on `/auth/me` before anything renders. A form
 * that loads for somebody who cannot save it is worse than one that says so.
 * This is not authorisation — the API decides that, on every request — it is
 * what stops the page offering an action it knows will be refused.
 */

export function GeneralPage() {
  const { t } = useTranslation(['app', 'common'])

  useDocumentTitle(`${t('settings.general.title')} · ${t('common:brand.name')}`)

  /**
   * Whether this account may edit the organisation at all.
   *
   * `usePermission` reads the map `/auth/me` returns, and lets `is_root` through
   * as the server does — otherwise the one account that can definitely save
   * would be the one told it cannot.
   *
   * Display only. The API re-decides on every request, and it is the only party
   * whose answer counts.
   */
  const mayManage = usePermission('account.manage').granted

  const account = useAccount({ enabled: mayManage })

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <header className="mb-5">
        <h1 className="m-0 text-headline">{t('settings.general.title')}</h1>
        <p className="mt-2 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
          {t('settings.general.intro')}
        </p>
      </header>

      {!mayManage ? (
        <Panel className="p-4">
          <p className="m-0 text-body-sm text-ink-dim">{t('settings.general.noPermission')}</p>
        </Panel>
      ) : account.isError ? (
        <ErrorState
          error={account.error}
          title={t('settings.general.loadFailed')}
          onRetry={() => account.refetch()}
        />
      ) : account.isPending ? (
        <SkeletonRows count={4} />
      ) : (
        <div className="flex flex-col gap-4">
          <IdentityCard account={account.data} />

          {/* The other three cards are commented out below, with the rest of
              the design's controls. Nothing on the account backs them. */}
        </div>
      )}
    </div>
  )
}

/* ── Helpers for the commented-out cards ──────────────────────────────────
   `useDraft`, `labelled` and `SaveRow` are used only by Regional & Locale,
   Platform Defaults and the Identity fields that have no endpoint. The card
   that survives holds one draft over the whole record, because `PUT /account`
   is a full replace and a per-field Save would submit values it did not show.
   Kept beside the code that needs them, so restoring a card is one uncomment.
   ─────────────────────────────────────────────────────────────────────── */

// /**
//  * A card with its own Save, which is the design's arrangement and the right one:
//  * these are four unrelated groups, and one Save over all of them would make
//  * changing a time zone re-submit the organisation's name.
//  *
//  * The draft derives rather than syncing in an effect — the pattern the Security
//  * page settled on, after this codebase got it wrong four times. `from` is the
//  * settings object the draft was started against; when the server sends a new
//  * one, the draft is stale by identity and empties itself.
//  */
// function useDraft(settings, keys) {
//   const [draft, setDraft] = useState({ from: settings, values: {} })
//   const values = draft.from === settings ? draft.values : {}
//
//   const value = (key) => (Object.hasOwn(values, key) ? values[key] : settings[key])
//   const set = (key, next) => setDraft({ from: settings, values: { ...values, [key]: next } })
//   const dirty = keys.some((key) => Object.hasOwn(values, key) && values[key] !== settings[key])
//   const payload = Object.fromEntries(
//     keys.filter((key) => Object.hasOwn(values, key)).map((key) => [key, values[key]]),
//   )
//
//   return { value, set, dirty, payload }
// }
//
// /** Options built from a list of ids, each labelled from one place. */
// const labelled = (t, prefix, ids = []) =>
//   ids.map((id) => ({ value: id, label: t(`${prefix}.${id}`) }))
//
// function SaveRow({ dirty, pending, onSave }) {
//   const { t } = useTranslation(['app', 'common'])
//   return (
//     <div className="mt-1 flex items-center gap-3">
//       <ForgeButton variant="primary" disabled={!dirty || pending} onClick={onSave}>
//         {t('settings.general.save')}
//       </ForgeButton>
//       {dirty ? (
//         <span className="text-micro font-normal text-ink-mute">
//           {t('settings.general.unsaved')}
//         </span>
//       ) : null}
//     </div>
//   )
// }
//
// /* ══ IDENTITY ════════════════════════════════════════════════════════════ */

/**
 * The organisation, as the API holds it: an alias, a name and a logo.
 *
 * One Save over all three rather than the design's per-card button, because
 * `PUT /account` is a full replace — `AccountUpdate` gives none of its fields a
 * default, so saving the name means sending the alias and the logo id with it.
 * A per-field Save would be a button that submits two values it did not show.
 */
function IdentityCard({ account }) {
  const { t } = useTranslation(['app', 'common'])
  const { toast } = useToast()
  const save = useSaveAccount()
  const picker = useRef(null)

  const [draft, setDraft] = useState({ alias: account.alias, name: account.name })
  const [errors, setErrors] = useState({})
  /* Staged, not uploaded. The picked file goes up as part of saving — see the
     profile dialog, which does the same for an avatar and for the same reason:
     a picked-then-cancelled image should leave nothing behind. */
  const [logo, setLogo] = useState({ preview: null, file: null })
  const [uploading, setUploading] = useState(false)

  const set = (key) => (event) =>
    setDraft((previous) => ({ ...previous, [key]: event.target.value }))

  const aliasChanging = draft.alias.trim() !== account.alias
  const dirty =
    aliasChanging ||
    draft.name.trim() !== account.name ||
    Boolean(logo.file)

  function pickLogo(file) {
    if (!file) return
    const problem = checkFile(file, FILE_PURPOSES.accountLogo)
    if (problem) {
      /* Whole keys, not `…identity.logo` plus a suffix. Built that way the key
         is a SIBLING of `identity.logo` rather than a child of it, which reads
         as nesting and is not — and `verify:i18n` cannot check a prefix that
         resolves to a string. */
      toast(
        problem.reason === 'size'
          ? t('settings.general.identity.logoTooBig', { limit: problem.limit })
          : t('settings.general.identity.logoNotImage', { limit: problem.limit }),
        'critical',
      )
      return
    }
    const reader = new FileReader()
    reader.onload = () => setLogo({ preview: String(reader.result), file })
    reader.onerror = () => toast(t('settings.general.identity.logoFailed'), 'critical')
    reader.readAsDataURL(file)
  }

  async function submit(event) {
    event.preventDefault()

    const next = {}
    if (!draft.alias.trim()) next.alias = t('settings.general.identity.aliasRequired')
    if (!draft.name.trim()) next.name = t('settings.general.identity.nameRequired')
    setErrors(next)
    if (Object.keys(next).length) return

    /* A new id attaches; the existing id is what "leave it alone" has to say
       out loud on a full replace. There is no detach — the logo can be
       replaced, not reverted to the default. */
    let logoFileId = account.logoFileId
    if (logo.file) {
      setUploading(true)
      try {
        logoFileId = await uploadFile(logo.file, FILE_PURPOSES.accountLogo)
      } catch (error) {
        setUploading(false)
        toast(
          error?.fields?.file === 'rejected'
            ? t('settings.general.identity.logoRejected')
            : t('settings.general.identity.logoUploadFailed'),
          'critical',
        )
        return
      }
      setUploading(false)
    }

    save.mutate(
      { alias: draft.alias, name: draft.name, logoFileId },
      {
        onSuccess: () => {
          setLogo({ preview: null, file: null })
          toast(
            aliasChanging
              ? t('settings.general.identity.savedAlias')
              : t('settings.general.saved'),
            'ok',
          )
        },
        onError: (error) => {
          if (error?.fields?.alias) setErrors({ alias: t('settings.general.identity.aliasTaken') })
          else toast(t('settings.general.saveFailed'), 'critical')
        },
      },
    )
  }

  return (
    <Panel className="p-4">
      <Eyebrow as="h2" className="mb-3">
        {t('settings.general.identity.title')}
      </Eyebrow>

      <form onSubmit={submit} noValidate className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_14rem]">
        <div className="flex flex-col gap-3.5">
          <Field
            id="org-name"
            label={t('settings.general.identity.name')}
            hint={t('settings.general.identity.nameHint')}
            required
            maxLength={150}
            value={draft.name}
            error={errors.name}
            onChange={set('name')}
          />

          {/* The login alias. Not on this page before, and it is the field with
              the longest reach on it: it is what everybody types to sign in, and
              changing it emails every active user in the organisation. Said
              plainly under the control rather than discovered afterwards. */}
          <Field
            id="org-alias"
            label={t('settings.general.identity.alias')}
            hint={
              aliasChanging
                ? t('settings.general.identity.aliasChanging')
                : t('settings.general.identity.aliasHint')
            }
            required
            maxLength={50}
            value={draft.alias}
            error={errors.alias}
            onChange={set('alias')}
            /* A machine identifier, so it is never auto-capitalised or
               spell-checked, and the API compares it exactly. */
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputClassName="font-mono"
          />

          {/* ── No endpoint behind these ──────────────────────────────────
              Display name, industry and tagline are not fields on the account.
              Left here, wired as they were, for whenever they become ones.

          <Field
            id="org-display-name"
            label={t('settings.general.identity.displayName')}
            hint={t('settings.general.identity.displayNameHint')}
            value={draft.value('displayName')}
            onChange={(event) => draft.set('displayName', event.target.value)}
          />
          <ForgeSelect
            id="org-industry"
            label={t('settings.general.identity.industry')}
            value={draft.value('industry')}
            onValueChange={(next) => draft.set('industry', next)}
            options={labelled(t, 'settings.general.industries', options.industries)}
          />
          <Field
            id="org-tagline"
            label={t('settings.general.identity.tagline')}
            value={draft.value('tagline') ?? ''}
            onChange={(event) => draft.set('tagline', event.target.value)}
          />
          */}

          <div className="mt-1 flex items-center gap-3">
            <ForgeButton
              type="submit"
              variant="primary"
              disabled={!dirty || save.isPending || uploading}
            >
              {uploading
                ? t('settings.general.identity.logoUploading')
                : save.isPending
                  ? t('settings.general.saving')
                  : t('settings.general.save')}
            </ForgeButton>
            {dirty ? (
              <span className="text-micro font-normal text-ink-mute">
                {t('settings.general.unsaved')}
              </span>
            ) : null}
          </div>
        </div>

        <LogoField
          currentUrl={account.logoUrl}
          logo={logo}
          onPick={() => picker.current?.click()}
          onUndo={() => setLogo({ preview: null, file: null })}
        />
        <input
          ref={picker}
          type="file"
          accept={acceptFor(FILE_PURPOSES.accountLogo)}
          className="sr-only"
          aria-label={t('settings.general.identity.logoChange')}
          onChange={(event) => {
            pickLogo(event.target.files?.[0])
            // Cleared so choosing the same file twice fires again.
            event.target.value = ''
          }}
        />
      </form>
    </Panel>
  )
}

/**
 * The organisation's mark, with a way to replace it.
 *
 * ── Where the default comes from ──────────────────────────────────────────
 * A bundled asset, imported so the bundler fingerprints it and it survives a
 * reload like any other part of the build. The record holds `null` for "the
 * default" rather than a path: a filename written into a fixture would be a
 * second place for this file's location to be wrong.
 *
 * ── A replacement is now a real upload ────────────────────────────────────
 * It used to be a data URL held in session state that never left the page,
 * because there was nothing to send it to. There is now: `logo_file_id` on the
 * account, and the three-call handshake in `features/files/api.js`. What is
 * shown before saving is still a local preview — the file goes up on Save, so a
 * picked-then-cancelled logo leaves nothing in the bucket.
 *
 * ── The URL on the record expires ─────────────────────────────────────────
 * `logo_view_url` is presigned with a five-minute life, which is why the account
 * query does not cache across visits and why nothing persists it.
 */
function LogoField({ currentUrl, logo, onPick, onUndo }) {
  const { t } = useTranslation(['app', 'common'])

  const shown = logo.preview ?? currentUrl
  const pending = Boolean(logo.file)

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-control text-ink">{t('settings.general.identity.logo')}</span>

      <div className="grid aspect-square place-items-center rounded-sm border border-hairline bg-sunken p-4">
        {/* `contain`, not `cover`: a logo cropped to fill a square is a logo with
            its edges cut off, and the whole point of the field is the mark. */}
        <img
          src={shown || defaultLogo}
          alt={t('settings.general.identity.logoAlt')}
          className="max-h-full max-w-full object-contain"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <ForgeButton size="sm" type="button" onClick={onPick}>
          {t('settings.general.identity.logoChange')}
        </ForgeButton>
        {/* Undo while something is staged. There is deliberately no "use the
            default": once replaced, the logo is replaced, not reverted. */}
        {pending ? (
          <ForgeButton size="sm" type="button" variant="ghost" onClick={onUndo}>
            {t('settings.general.identity.logoUndo')}
          </ForgeButton>
        ) : null}
      </div>

      <p className="m-0 text-micro font-normal text-ink-mute">
        {logo.file
          ? t('settings.general.identity.logoStaged')
          : t('settings.general.identity.logoHint')}
      </p>
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════════════════
   EVERYTHING BELOW HAS NO ENDPOINT BEHIND IT

   Regional & Locale, Platform Defaults and the Danger Zone were all answered by
   the fixture-backed `/org/settings`, which the real API does not serve. They
   are commented out rather than deleted: this is the design's page, the code is
   correct for the contract it was written against, and restoring a card when
   its fields land should be uncommenting a block rather than rebuilding it.

   Regional has a second reason. The product holds a time zone, date order and
   clock on the USER and on the PLANT — `DisplayPreferenceFields` is mixed into
   those two schemas and nothing else. There is no organisation-level set to
   edit. The user's own copy is real and lives in the profile dialog off the nav
   rail; an org-level set here would be a second control over a preference only
   one of them can write.
   ══════════════════════════════════════════════════════════════════════════ */

// const REGIONAL_KEYS = ['timeZone', 'dateFormat', 'timeFormat', 'language']
//
// function RegionalCard({ settings, options, siteUnits, pending, onSave }) {
//   const { t } = useTranslation(['app', 'common'])
//   const draft = useDraft(settings, REGIONAL_KEYS)
//
//   return (
//     <Panel className="p-4">
//       <Eyebrow as="h2" className="mb-1">
//         {t('settings.general.regional.title')}
//       </Eyebrow>
//       <p className="mt-0 mb-3.5 text-body-sm text-ink-dim">
//         {t('settings.general.regional.intro')}
//       </p>
//
//       <div className="grid gap-3.5 md:grid-cols-3">
//         <ForgeSelect
//           id="org-timezone"
//           label={t('settings.general.regional.timeZone')}
//           hint={t('settings.general.regional.timeZoneHint')}
//           value={draft.value('timeZone')}
//           onValueChange={(next) => draft.set('timeZone', next)}
//           /* A time zone is an IANA identifier — a machine string, the same in
//              every locale — so it is printed rather than translated. */
//           options={options.timeZones.map((zone) => ({ value: zone, label: zone }))}
//         />
//         <ForgeSelect
//           id="org-date-format"
//           label={t('settings.general.regional.dateFormat')}
//           value={draft.value('dateFormat')}
//           onValueChange={(next) => draft.set('dateFormat', next)}
//           options={labelled(t, 'settings.general.dateFormats', options.dateFormats)}
//         />
//         <ForgeSelect
//           id="org-time-format"
//           label={t('settings.general.regional.timeFormat')}
//           value={draft.value('timeFormat')}
//           onValueChange={(next) => draft.set('timeFormat', next)}
//           options={labelled(t, 'settings.general.timeFormats', options.timeFormats)}
//         />
//         <ForgeSelect
//           id="org-language"
//           label={t('settings.general.regional.language')}
//           hint={
//             SUPPORTED_LOCALES.length === 1 ? t('settings.general.regional.languageOne') : undefined
//           }
//           value={draft.value('language')}
//           onValueChange={(next) => draft.set('language', next)}
//           options={labelled(t, 'settings.general.languages', SUPPORTED_LOCALES)}
//         />
//         {/* Disabled in place. The reason is below, and it names what is
//             actually in force at each site rather than only refusing. */}
//         <ForgeSelect
//           id="org-units"
//           label={t('settings.general.regional.units')}
//           value="perSite"
//           disabled
//           options={[{ value: 'perSite', label: t('settings.general.regional.unitsPerSite') }]}
//         />
//       </div>
//
//       <p className="mt-3 mb-0 text-micro font-normal text-ink-mute">
//         {t('settings.general.regional.unitsReason', {
//           sites: siteUnits
//             .map((site) =>
//               t('settings.general.regional.siteUnit', {
//                 site: site.name,
//                 units: t(`settings.general.unitSystems.${site.unitSystem}`),
//               }),
//             )
//             .join(' · '),
//         })}
//       </p>
//
//       <SaveRow dirty={draft.dirty} pending={pending} onSave={() => onSave(draft.payload)} />
//     </Panel>
//   )
// }
//
// /* ══ PLATFORM DEFAULTS ═══════════════════════════════════════════════════ */
//
// const DEFAULT_KEYS = ['landingPage', 'defaultSiteId', 'dataRetention']
//
// function DefaultsCard({ settings, options, sessionTimeout, pending, onSave }) {
//   const { t } = useTranslation(['app', 'common'])
//   const draft = useDraft(settings, DEFAULT_KEYS)
//
//   return (
//     <Panel className="p-4">
//       <Eyebrow as="h2" className="mb-3">
//         {t('settings.general.defaults.title')}
//       </Eyebrow>
//
//       <div className="grid gap-3.5 md:grid-cols-2">
//         <ForgeSelect
//           id="org-landing"
//           label={t('settings.general.defaults.landingPage')}
//           hint={t('settings.general.defaults.landingPageHint')}
//           value={draft.value('landingPage')}
//           onValueChange={(next) => draft.set('landingPage', next)}
//           /* Named from the navigation rail's own labels, so a page cannot be
//              offered here under a name it does not have there. */
//           options={options.landingPages.map((page) => ({
//             value: page.value,
//             label: t(`screens.${page.value}`),
//           }))}
//         />
//         <ForgeSelect
//           id="org-default-site"
//           label={t('settings.general.defaults.defaultSite')}
//           hint={t('settings.general.defaults.defaultSiteHint')}
//           value={draft.value('defaultSiteId')}
//           onValueChange={(next) => draft.set('defaultSiteId', next)}
//           options={options.sites}
//         />
//
//         {/* The Security page's value, printed rather than offered a second
//             time. One setting, one control, and a way to get to it. */}
//         <div className="flex flex-col gap-1.5">
//           <span className="text-control text-ink">
//             {t('settings.general.defaults.sessionTimeout')}
//           </span>
//           <p className="m-0 flex h-[38px] items-center rounded-sm border border-hairline bg-sunken px-3 text-body-sm text-ink-dim">
//             {t(`settings.security.sessions.timeouts.${sessionTimeout}`)}
//           </p>
//           <p className="m-0 text-micro font-normal text-ink-mute">
//             {t('settings.general.defaults.sessionTimeoutOwner')}{' '}
//             <Link to="/app/settings/security" className="text-on-forge underline">
//               {t('settings.pages.security')}
//             </Link>
//           </p>
//         </div>
//
//         <ForgeSelect
//           id="org-retention"
//           label={t('settings.general.defaults.dataRetention')}
//           hint={t('settings.general.defaults.dataRetentionHint')}
//           value={draft.value('dataRetention')}
//           onValueChange={(next) => draft.set('dataRetention', next)}
//           options={labelled(t, 'settings.general.retention', options.dataRetention)}
//         />
//       </div>
//
//       <SaveRow dirty={draft.dirty} pending={pending} onSave={() => onSave(draft.payload)} />
//     </Panel>
//   )
// }
//
// /* ══ DANGER ══════════════════════════════════════════════════════════════ */
//
// function DangerZone() {
//   const { t } = useTranslation(['app', 'common'])
//   const { toast } = useToast()
//   const reset = useResetSettings()
//
//   const [confirming, setConfirming] = useState(false)
//   const [exporting, setExporting] = useState(false)
//
//   async function runExport() {
//     setExporting(true)
//     try {
//       const data = await fetchExport()
//       downloadJson(`forge-export-${data.exportedAt.slice(0, 10)}.json`, data)
//       toast(t('settings.general.danger.exported'), 'ok')
//     } catch {
//       toast(t('settings.general.danger.exportFailed'), 'critical')
//     } finally {
//       setExporting(false)
//     }
//   }
//
//   return (
//     <Panel stripe="red" className="p-4">
//       <Eyebrow as="h2" className="mb-1 text-on-red">
//         {t('settings.general.danger.title')}
//       </Eyebrow>
//       <p className="mt-0 mb-3.5 text-body-sm text-ink-dim">
//         {t('settings.general.danger.intro')}
//       </p>
//
//       <ul className="m-0 flex list-none flex-col p-0">
//         <DangerRow
//           title={t('settings.general.danger.reset')}
//           body={t('settings.general.danger.resetBody')}
//           action={
//             <ForgeButton
//               className="text-on-red hover:text-on-red"
//               disabled={reset.isPending}
//               onClick={() => setConfirming(true)}
//             >
//               {t('settings.general.danger.resetAction')}
//             </ForgeButton>
//           }
//         />
//         <DangerRow
//           title={t('settings.general.danger.export')}
//           body={t('settings.general.danger.exportBody')}
//           action={
//             <ForgeButton disabled={exporting} onClick={runExport}>
//               {t('settings.general.danger.exportAction')}
//             </ForgeButton>
//           }
//         />
//         {/* In place, disabled, with the reason. This is the one control on the
//             page where pretending would be unforgivable: it names the account
//             this session is signed in as. */}
//         <DangerRow
//           title={t('settings.general.danger.delete')}
//           body={t('settings.general.danger.deleteUnavailable')}
//           action={
//             <ForgeButton variant="danger" disabled>
//               {t('settings.general.danger.deleteAction')}
//             </ForgeButton>
//           }
//         />
//       </ul>
//
//       {confirming ? (
//         <ForgeDialog
//           open
//           onOpenChange={(next) => !next && setConfirming(false)}
//           size="sm"
//           title={t('settings.general.danger.resetTitle')}
//           footer={
//             <>
//               <ForgeButton onClick={() => setConfirming(false)}>
//                 {t('common:actions.cancel')}
//               </ForgeButton>
//               <span className="flex-1" />
//               <ForgeButton
//                 variant="danger"
//                 disabled={reset.isPending}
//                 onClick={() =>
//                   reset.mutate(undefined, {
//                     onSuccess: () => {
//                       toast(t('settings.general.danger.resetDone'), 'ok')
//                       setConfirming(false)
//                     },
//                     onError: () => toast(t('settings.general.danger.resetFailed'), 'critical'),
//                   })
//                 }
//               >
//                 {t('settings.general.danger.resetAction')}
//               </ForgeButton>
//             </>
//           }
//         >
//           {/* What goes back, named. And what does not. */}
//           <p className="mt-0 mb-2 text-body-sm text-ink-dim">
//             {t('settings.general.danger.resetConfirm')}
//           </p>
//           <p className="m-0 text-body-sm text-ink-dim">
//             {t('settings.general.danger.resetKeeps')}
//           </p>
//         </ForgeDialog>
//       ) : null}
//     </Panel>
//   )
// }
//
// function DangerRow({ title, body, action }) {
//   return (
//     <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-hairline py-3 first:pt-0 last:border-b-0 last:pb-0">
//       <div className="min-w-0 flex-1">
//         <p className="m-0 text-body-sm font-semibold text-ink">{title}</p>
//         <p className="m-0 text-micro font-normal text-ink-mute">{body}</p>
//       </div>
//       {action}
//     </li>
//   )
// }
